import { applyPatch, structuredPatch } from "diff";

/**
 * The rich-text editor re-serializes the whole document on every change: it adds
 * backslash escapes (`=` -> `\=`, `[` -> `\[`, `5).` -> `5\).`), renumbers list
 * markers and reformats citations and tables. Saving that output would rewrite
 * parts the user never touched.
 *
 * Instead, the change the user made (editor output before vs. after the edit, both
 * from the same serializer) is applied as a patch onto the original source. Lines
 * are matched on their letters and digits only, so the serializer's cosmetic
 * differences don't stop the patch from finding its place. Everything outside the
 * edited lines stays byte-for-byte as it was. If the patch cannot be placed, the
 * editor's own output is returned so the user's edit is never lost.
 */

const LIST_MARKER = /^\s*(?:[-*+]|\d+[.)])\s+/;
const NOT_WORD = /[^\p{L}\p{N}]+/gu;

const MD_ESCAPE = /\\(?=[!-/:-@[-`{-~])/g;

const lineKey = (line: string): string =>
    line.replace(/&#x20;/g, " ").replace(MD_ESCAPE, "").replace(LIST_MARKER, "").replace(NOT_WORD, "").toLowerCase();

/** Drop the escapes the serializer adds where markdown would read the text the same without them. */
const dropNeedlessEscapes = (line: string): string =>
    (/^\s*\\?=[\\=\s]*$/.test(line) ? line : line.replace(/\\=/g, "="))
        .replace(/([\p{L}\p{N}])\\_(?=[\p{L}\p{N}])/gu, "$1_")
        .replace(/\\\[([^\]\n]*)\](?![([:])/g, "[$1]");

const tidy = (markdown: string): string =>
    markdown.replace(/&#x20;/g, " ").replace(/\n+$/, "");

const CITATION = /\$\{(?:A|E|D|H):[0-9]+,\s*[0-9]+,\s*[0-9]+,\s*"(?:[^"\\}]|\\.|"(?!}))*"\}/g;
const QUOTE_PUNCTUATION = /[!#%&'()*+\-./:;<=>?@[\]^_`|~]/;
const MONTHS = "januar|februar|marts|march|april|maj|may|juni|june|juli|july|august|september|oktober|october|november|december|january|february";
const DATE_LINE = new RegExp(`^(\\s*(?:[-*+]\\s+)?)(\\d{1,2})\\.(\\s+(?:${MONTHS})\\b)`, "gimu");

/** Backslash-escape markdown punctuation inside a citation's quote, so the parser
 *  keeps it as text (the escapes are consumed on parse; the quote is unchanged). */
const protectQuote = (citation: string): string => {
    const open = citation.indexOf('"') + 1;
    let out = citation.slice(0, open);
    const body = citation.slice(open, -2);
    for (let i = 0; i < body.length; i++) {
        const ch = body[i];
        if (ch === "\\") {
            out += ch + (body[i + 1] ?? "");
            i++;
        } else {
            out += QUOTE_PUNCTUATION.test(ch) ? "\\" + ch : ch;
        }
    }
    return out + '"}';
};

/**
 * Markdown to load into the editor. Citation quotes and Danish date lines are
 * escaped so they display as written: `21. september` at a line start would
 * otherwise become an ordered list (and `6. oktober` after it item 22), and
 * `*` inside a quote would become emphasis and drop out of the citation.
 */
export function prepareForEditor(markdown: string): string {
    return markdown.replace(CITATION, protectQuote).replace(DATE_LINE, "$1$2\\.$3");
}

export function mergeEditorChange(source: string, baseline: string, current: string): string {
    const base = tidy(baseline);
    const next = tidy(current);
    if (base === next) return source;

    const trailing = source.match(/\n*$/)?.[0] ?? "";
    const patch = structuredPatch("source", "source", base, next, undefined, undefined, { context: 2 });
    for (const hunk of patch.hunks) {
        hunk.lines = hunk.lines.map((l) => (l.startsWith("+") ? "+" + dropNeedlessEscapes(l.slice(1)) : l));
    }
    const merged = applyPatch(source.replace(/\n+$/, ""), patch, {
        compareLine: (_lineNumber, line, _operation, patchContent) => lineKey(line) === lineKey(patchContent),
    });
    if (merged === false) {
        console.warn("Rich text: could not keep the original markdown around the edit; saving editor output.");
        return current;
    }
    return merged + trailing;
}
